import React from "react";
import { fmt } from "./Utils/Utilities";

// ─── Status badge with colour coding ─────────────────────────────────────────
const STATUS_CFG = {
  Delivered: {
    color: "var(--success)",
    bg: "rgba(0,230,118,.08)",
    border: "rgba(0,230,118,.2)",
    dot: "var(--success)",
    label: "Delivered",
  },
  Processing: {
    color: "var(--flame)",
    bg: "rgba(255,79,23,.08)",
    border: "rgba(255,79,23,.2)",
    dot: "var(--flame)",
    label: "Processing",
  },
  Pending: {
    color: "var(--amber)",
    bg: "rgba(255,171,0,.08)",
    border: "rgba(255,171,0,.2)",
    dot: "var(--amber)",
    label: "Pending",
  },
  Cancelled: {
    color: "var(--danger)",
    bg: "rgba(255,23,68,.08)",
    border: "rgba(255,23,68,.2)",
    dot: "var(--danger)",
    label: "Cancelled",
  },
  Dispatched: {
    color: "var(--blue)",
    bg: "rgba(64,196,255,.08)",
    border: "rgba(64,196,255,.2)",
    dot: "var(--blue)",
    label: "Dispatched",
  },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.Pending;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 20,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        color: cfg.color,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: ".04em",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      {cfg.label}
    </span>
  );
}

// ─── Small data cell ──────────────────────────────────────────────────────────
function Cell({ label, value, mono = false, accent = false, large = false }) {
  return (
    <div>
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: ".1em",
          textTransform: "uppercase",
          color: "var(--text3)",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: large ? 18 : 13,
          fontWeight: accent ? 800 : 600,
          fontFamily: mono ? "inherit" : "inherit",
          color: accent ? "var(--flame)" : "var(--text1)",
          lineHeight: 1.2,
        }}
      >
        {value || "—"}
      </div>
    </div>
  );
}

// ─── Section label ────────────────────────────────────────────────────────────
function Section({ title, children, style = {} }) {
  return (
    <div style={{ marginBottom: 20, ...style }}>
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: ".14em",
          textTransform: "uppercase",
          color: "var(--text3)",
          paddingBottom: 10,
          marginBottom: 14,
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
function OrderDetails({ order }) {
  if (!order) return null;

  const total = order.total ?? Number(order.qty) * Number(order.unitPrice);
  const hasNotes = order.notes && order.notes.trim().length > 0;

  return (
    <div style={{ minWidth: 480, maxWidth: 1600 }}>
      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 22,
          paddingBottom: 18,
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: ".12em",
              textTransform: "uppercase",
              color: "var(--text3)",
              marginBottom: 5,
            }}
          >
            Order Reference
          </div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: "var(--flame)",
              fontFamily: "inherit",
              letterSpacing: ".02em",
              lineHeight: 1,
            }}
          >
            {order.id}
          </div>
          {order.delivery && (
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 5 }}>
              Delivery · {fmt.date(order.delivery)}
            </div>
          )}
        </div>
        <StatusBadge status={order.status} />
      </div>

      {/* ── Customer ── */}
      <Section title="Customer">
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}
        >
          <Cell label="Name" value={order.customer} />
          <Cell label="Region" value={order.region} />
          {order.customerId && (
            <Cell label="Customer ID" value={order.customerId} mono />
          )}
          {order.driver && (
            <Cell label="Assigned Driver" value={order.driver} />
          )}
        </div>
      </Section>

      {/* ── Product ── */}
      <Section title="Product Details">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 14,
            marginBottom: 14,
          }}
        >
          <Cell label="Product" value={order.product} />
          <Cell
            label="Quantity"
            value={
              order.qty
                ? `${fmt.number(order.qty)} ${order.unit || ""}`.trim()
                : null
            }
            mono
          />
          <Cell
            label="Unit Price"
            value={
              order.unitPrice ? fmt.currency(Number(order.unitPrice)) : null
            }
            mono
          />
        </div>

        {/* Total — highlighted row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 16px",
            borderRadius: "var(--r8)",
            background: "rgba(255,79,23,.06)",
            border: "1px solid rgba(255,79,23,.15)",
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: ".1em",
              textTransform: "uppercase",
              color: "var(--flame)",
            }}
          >
            Order Total
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 900,
              color: "var(--flame)",
              fontFamily: "inherit",
              letterSpacing: "-.01em",
            }}
          >
            {fmt.currency(total)}
          </div>
        </div>
      </Section>

      {/* ── Notes ── */}
      <Section title="Notes" style={{ marginBottom: 0 }}>
        {hasNotes ? (
          <div
            style={{
              fontSize: 12,
              color: "var(--text2)",
              lineHeight: 1.6,
              padding: "10px 14px",
              borderRadius: "var(--r8)",
              background: "var(--deep)",
              border: "1px solid var(--border)",
            }}
          >
            {order.notes}
          </div>
        ) : (
          <div
            style={{
              fontSize: 12,
              color: "var(--text3)",
              fontStyle: "italic",
              padding: "10px 14px",
              borderRadius: "var(--r8)",
              background: "var(--deep)",
              border: "1px solid var(--border)",
            }}
          >
            No notes on this order.
          </div>
        )}
      </Section>
    </div>
  );
}

export default OrderDetails;
